import { motion } from "framer-motion";

export default function UIUX() {
  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
  };

  return (
    <div className="bg-black text-gray-100 overflow-hidden">

      {/* ================= HERO ================= */}
      <section className="relative h-screen flex items-center justify-center text-center">
        <motion.div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?auto=format&fit=crop&w=1800&q=80')",
          }}
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2 }}
        />
        <div className="absolute inset-0 bg-black/80" />

        <motion.div
          className="relative z-10 max-w-4xl px-6"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6">
            UI / UX <span className="text-orange-500">Design</span>
          </h1>
          <p className="text-gray-300 text-lg">
            Designing intuitive, beautiful, and conversion-focused digital
            experiences.
          </p>
        </motion.div>
      </section>

      {/* ================= STATS ================= */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-4 gap-8 text-center">
          {[
            { n: "200+", t: "Screens Designed" },
            { n: "80+", t: "Happy Clients" },
            { n: "5+ Years", t: "Design Experience" },
            { n: "98%", t: "User Satisfaction" },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <h3 className="text-4xl font-extrabold text-orange-500">{s.n}</h3>
              <p className="text-gray-400 mt-2">{s.t}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= ABOUT ================= */}
      <motion.section
        className="py-24 max-w-6xl mx-auto px-6"
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <motion.h2 className="text-3xl font-bold text-orange-500 mb-6" variants={fadeUp}>
          Our Design Philosophy
        </motion.h2>
        <motion.p className="text-lg text-gray-300" variants={fadeUp}>
          We believe great design is not just how it looks, but how it works.
          Our UI/UX process is rooted in user psychology, accessibility, and
          business goals to deliver experiences users love.
        </motion.p>
      </motion.section>

      {/* ================= SERVICES ================= */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-orange-500 text-center mb-12">
            UI / UX Design Services
          </h2>

          <div className="grid md:grid-cols-2 gap-10">
            {[
              "User Research & Personas",
              "Wireframes & Prototypes",
              "Mobile & Web UI Design",
              "Design Systems & UI Kits",
            ].map((item, i) => (
              <motion.div
                key={i}
                className="p-8 rounded-2xl shadow bg-gray-900"
                initial={{ opacity: 0, x: i % 2 === 0 ? -40 : 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-semibold mb-3">{item}</h3>
                <p className="text-gray-400">
                  User-focused, modern and scalable {item.toLowerCase()} for
                  digital products.
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TOOLS ================= */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-orange-500 text-center mb-12">
            Tools We Use
          </h2>
          <div className="grid md:grid-cols-6 gap-6 text-center">
            {["Figma", "Adobe XD", "Sketch", "Photoshop", "Illustrator", "Framer"].map(
              (tool, i) => (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.1 }}
                  className="p-6 bg-black/40 rounded-xl"
                >
                  {tool}
                </motion.div>
              )
            )}
          </div>
        </div>
      </section>

      {/* ================= DESIGN PROCESS ================= */}
      <section className="py-24 max-w-6xl mx-auto px-6">
        <h2 className="text-3xl font-bold text-orange-500 text-center mb-12">
          Our Design Process
        </h2>

        <div className="grid md:grid-cols-4 gap-6">
          {["Research", "Wireframe", "Visual Design", "Testing"].map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl shadow bg-gray-900"
            >
              <h3 className="font-semibold mb-2">{step}</h3>
              <p className="text-sm text-gray-400">
                {step === "Research" && "Understanding users & goals"}
                {step === "Wireframe" && "Structuring layouts"}
                {step === "Visual Design" && "High-fidelity UI"}
                {step === "Testing" && "Usability & improvements"}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

     

      {/* ================= FAQ ================= */}
      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-orange-500 text-center mb-12">
            FAQs
          </h2>

          {[
            "Do you follow UX research?",
            "Can you redesign existing apps?",
            "Do you provide design systems?",
          ].map((q, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-6 p-6 rounded-xl shadow bg-gray-900"
            >
              <h3 className="font-semibold mb-2">{q}</h3>
              <p className="text-gray-400">
                Yes, our UI/UX services cover complete product design lifecycle.
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= CTA ================= */}
      <motion.section
        className="py-28 text-center bg-gradient-to-r from-orange-600 to-orange-900 text-white"
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="text-4xl font-extrabold mb-6">
          Let’s Design Something Beautiful
        </h2>
        <p className="text-orange-100 mb-10">
          Transform ideas into delightful user experiences.
        </p>
        <a
          href="/contact"
          className="inline-block px-12 py-4 rounded-full bg-black text-orange-400 font-semibold"
        >
          Start Design
        </a>
      </motion.section>
    </div>
  );
}
